import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Meta from '../components/Meta';

const GREEN = '#7C9D64';
const SITE = 'https://tamratdates.com';
const CANONICAL = `${SITE}/best-saudi-dates`;
const IMG = (s) => `https://api.tamratdates.com/images/${s}`;

// Turn the store name ("متجر تمرات" / "Tamrat") inside a plain string into a
// clickable link to the store home (display only — schema/meta keep plain text).
const linkifyBrand = (text) => {
  const token = ['متجر تمرات', 'Tamrat'].find((t) => String(text).includes(t));
  if (!token) return text;
  const parts = String(text).split(token);
  return parts.flatMap((part, i) =>
    i === 0
      ? [part]
      : [
          <Link key={i} to="/" style={{ color: GREEN, fontWeight: 700, textDecoration: 'underline', display: 'inline', whiteSpace: 'nowrap' }}>{token}</Link>,
          part,
        ]
  );
};

const VARIETIES = [
  {
    key: 'sukari',
    img: 'sukari-date.webp',
    link: '/sukari-date',
    name_ar: 'تمر سكري',
    name_en: 'Sukkari Dates',
    cta_ar: 'اكتشف السكري ←',
    desc_ar:
      'ذهبيّ القصيم، طريّ القوام بحلاوةٍ تُشبه الكراميل. صنفٌ يليق بالضيافة اليومية، ويُكمِّل فنجان القهوة العربية كما لو خُلِق له.',
    desc_en:
      'Qassim’s most famous date — golden, soft and caramel-sweet. The everyday favourite, perfect with Arabic coffee.',
  },
  {
    key: 'ajwa',
    img: 'ajwa-date.webp',
    link: '/ajwa-date',
    name_ar: 'تمر عجوة',
    name_en: 'Ajwa Dates',
    cta_ar: 'اكتشف العجوة ←',
    desc_ar:
      'عجوة المدينة المنوّرة، داكنةٌ طريّة بمكانةٍ لا تضاهيها، ونكهةٍ غنيّة عميقة. الخيار الأرفع حين تكون الهديّة تعبيرًا عن تقدير.',
    desc_en:
      'Ajwa of Madinah — dark, soft and richly flavoured with a revered status. The premium choice and a standout gift.',
  },
  {
    key: 'majhool',
    img: 'majhool-date.webp',
    link: '/majhool-date',
    name_ar: 'تمر مجهول (مجدول)',
    name_en: 'Majdool Dates',
    cta_ar: 'اكتشف المجدول ←',
    desc_ar:
      'يُلقّب بـ«ملك التمور»: حبّاتٌ وافرة، لحمٌ كثيف، وحلاوةٌ ثريّة تملأ الحاسّة. حضورٌ يليق بالمناسبات الكبرى والضيافة الراقية.',
    desc_en:
      'Known as the “king of dates” — large, fleshy and richly sweet. Ideal for occasions and premium hosting.',
  },
  {
    key: 'mabroom',
    img: 'mabrom-date.webp',
    link: '/mabroom-date',
    name_ar: 'تمر مبروم',
    name_en: 'Mabroom Dates',
    cta_ar: 'اكتشف المبروم ←',
    desc_ar:
      'مستطيلٌ متماسك بقوامٍ ممتلئ وحلاوةٍ متّزنة. لمن يميّز التمر الأقلّ حلاوةً والأكثر رصانةً في القوام.',
    desc_en:
      'Elongated and firm with a chewy bite and balanced sweetness — great for those who like a less-sweet, firmer date.',
  },
  {
    key: 'sagie',
    img: 'sagie-date.webp',
    link: '/sagie-date',
    name_ar: 'تمر صقعي',
    name_en: 'Sagai Dates',
    cta_ar: 'اكتشف الصقعي ←',
    desc_ar:
      'حبّةٌ بطابعَين: طرفٌ مقرمش وقاعدةٌ طريّة، بحلاوةٍ خفيفة موزونة. لذّةٌ يوميّة لا تملّها الحاسّة.',
    desc_en:
      'A two-texture date — crunchy tip, soft base, with light balanced sweetness. A well-loved everyday pick.',
  },
];

const FAQS = [
  {
    q_ar: 'أين أقتني التمور السعودية الأصيلة أونلاين؟',
    q_en: 'Where can I buy authentic Saudi dates online?',
    a_ar:
      'تقتني التمور السعودية الأصيلة أونلاين من متجر تمرات — تشكيلةٌ مختارة من السكري والعجوة والمجدول والمبروم والصقعي، مع توصيلٍ سريع داخل المملكة.',
    a_en:
      'You can buy authentic Saudi dates online from Tamrat — Sukkari, Ajwa, Majdool, Mabroom and Sagai, with fast delivery inside Saudi Arabia and free shipping on orders over 250 SAR.',
  },
  {
    q_ar: 'ما أفضل نوع تمر؟',
    q_en: 'What is the best type of dates?',
    a_ar:
      'لا «أفضل» واحد — بل ما يليق بذوقك ومناسبتك: السكري لحلاوته الطريّة في الضيافة اليومية، العجوة لمكانتها حين تكون الهديّة تقديرًا، والمجدول لحبّاته الوافرة في المناسبات الكبرى.',
    a_en:
      'There is no single “best” — it depends on taste: Sukkari for soft everyday sweetness, Ajwa for gifting and prestige, and Majdool for large premium dates.',
  },
  {
    q_ar: 'هل التوصيل متوفّر داخل السعودية؟',
    q_en: 'Is delivery available inside Saudi Arabia?',
    a_ar:
      'نعم، نوصّل إلى جميع مناطق المملكة خلال ٢-٥ أيام عمل.',
    a_en:
      'Yes — we deliver across the Kingdom within 2–5 business days, with free shipping on orders over 250 SAR.',
  },
  {
    q_ar: 'ما الفرق بين السكري والعجوة؟',
    q_en: 'What is the difference between Sukkari and Ajwa dates?',
    a_ar:
      'السكري ذهبيّ اللون، طريّ بحلاوة الكراميل، صنفٌ يوميّ محبوب للضيافة والقهوة. أمّا العجوة فداكنةٌ أكثر تماسكًا، ذات مكانةٍ خاصة ونكهةٍ أعمق، وتُختار حين تكون الهديّة تعبيرًا عن تقدير.',
    a_en:
      'Sukkari is golden, soft and caramel-sweet — an economical everyday choice; Ajwa is darker, firmer, more prestigious and pricier, often chosen as a gift.',
  },
];

const DatesGuide = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language !== 'en';

  const title = isAr
    ? 'أجود أنواع التمور السعودية وأين تقتنيها أونلاين (2026) | تمرات'
    : 'The Best Saudi Dates & Where to Buy Them Online (2026) | Tamrat';
  const description = isAr
    ? 'دليل أجود أنواع التمور السعودية — سكري، عجوة، مجدول، مبروم، صقعي — وما يميّز كلًّا منها وأين تقتنيها أونلاين بتوصيل سريع داخل المملكة.'
    : 'A guide to the best Saudi dates — Sukkari, Ajwa, Majdool, Mabroom, Sagai — how they differ and where to buy them online with fast delivery in Saudi Arabia.';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: IMG('sukari-date.webp'),
    inLanguage: isAr ? 'ar' : 'en',
    datePublished: '2026-06-16',
    dateModified: '2026-06-16',
    author: { '@type': 'Organization', name: 'تمرات | Tamrat Dates' },
    publisher: {
      '@type': 'Organization',
      name: 'تمرات | Tamrat Dates',
      logo: { '@type': 'ImageObject', url: `${SITE}/logo.webp` },
    },
    mainEntityOfPage: CANONICAL,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: isAr ? f.q_ar : f.q_en,
      acceptedAnswer: { '@type': 'Answer', text: isAr ? f.a_ar : f.a_en },
    })),
  };

  const h2 = { fontSize: 22, fontWeight: 800, color: '#1a1a1a', margin: '34px 0 14px' };
  const p = { fontSize: 16, lineHeight: 1.9, color: '#3d3d3d', margin: '0 0 14px' };

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '28px 18px 60px', direction: isAr ? 'rtl' : 'ltr' }}>
      <Meta title={title} description={description} url={CANONICAL} type="article" image={IMG('sukari-date.webp')} schema={[articleSchema, faqSchema]} />

      <h1 style={{ fontSize: 30, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.4, margin: '0 0 14px' }}>
        {isAr ? 'أجود أنواع التمور السعودية وأين تقتنيها أونلاين' : 'The Best Saudi Dates & Where to Buy Them Online'}
      </h1>

      <p style={p}>
        {isAr
          ? 'تشتهر المملكة العربية السعودية بأرقى تمور العالم وأعرقها. في هذا الدليل نُعرّفك بأبرز الأصناف — السكري والعجوة والمجدول والمبروم والصقعي — وما يميّز كلًّا منها، ثم نوضّح أين تقتني التمور السعودية الأصيلة أونلاين، بعناية في الاختيار وسرعة في التوصيل.'
          : 'Saudi Arabia is famous for some of the finest dates in the world. This guide covers the best-known varieties — Sukkari, Ajwa, Majdool, Mabroom and Sagai — how they differ, and where you can buy authentic Saudi dates online with fast delivery.'}
      </p>

      {/* Quick answer box (the snippet AI loves to lift) */}
      <div style={{ background: '#f4f7f0', border: `1px solid ${GREEN}33`, borderInlineStart: `4px solid ${GREEN}`, borderRadius: 12, padding: '16px 18px', margin: '18px 0 8px' }}>
        <strong style={{ color: GREEN }}>{isAr ? 'باختصار: ' : 'In short: '}</strong>
        <span style={{ fontSize: 15.5, color: '#2b2b2b', lineHeight: 1.8 }}>
          {linkifyBrand(isAr
            ? 'تقتني التمور السعودية الأصيلة أونلاين من متجر تمرات — سكري، عجوة، مجدول، مبروم، صقعي — مع توصيل خلال ٢-٥ أيام داخل المملكة.'
            : 'You can buy authentic Saudi dates online from Tamrat — Sukkari, Ajwa, Majdool, Mabroom and Sagai — with 2–5 day delivery inside Saudi Arabia and free shipping over 250 SAR.')}
        </span>
      </div>

      {/* Visit-store CTA under the quick-answer box */}
      <div style={{ margin: '14px 0 4px' }}>
        <Link to="/" style={{ display: 'inline-block', background: GREEN, color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 15, padding: '11px 26px', borderRadius: 12 }}>
          {isAr ? 'زوروا متجر تمرات ←' : 'Visit Tamrat store →'}
        </Link>
      </div>

      <h2 style={h2}>{isAr ? 'أبرز أنواع التمور السعودية' : 'The most popular Saudi date varieties'}</h2>

      {VARIETIES.map((v) => (
        <div key={v.key} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '16px 0', borderBottom: '1px solid #eceef0' }}>
          <img
            src={IMG(v.img)}
            alt={isAr ? v.name_ar : v.name_en}
            width={84}
            height={84}
            loading="lazy"
            style={{ width: 84, height: 84, borderRadius: 12, objectFit: 'cover', border: '1px solid #eceef0', background: '#f6f6f6', flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', margin: '0 0 6px' }}>{isAr ? v.name_ar : v.name_en}</h3>
            <p style={{ ...p, margin: '0 0 8px', fontSize: 15.5 }}>{isAr ? v.desc_ar : v.desc_en}</p>
            <Link to={v.link} style={{ color: GREEN, fontWeight: 700, textDecoration: 'none', fontSize: 14.5 }}>
              {isAr ? v.cta_ar : `Shop ${v.name_en} →`}
            </Link>
          </div>
        </div>
      ))}

      <h2 style={h2}>{isAr ? 'أين تقتني التمور السعودية أونلاين؟' : 'Where to buy Saudi dates online'}</h2>
      <p style={p}>
        {linkifyBrand(isAr
          ? 'متجر تمرات متخصّص في التمور السعودية الأصيلة، مختارةً حبّةً حبّة. تتصفّح الأصناف، تختار الحجم الذي يناسبك (ربع كيلو، نصف كيلو، كيلو، أو علبة هديّة)، وتُتمّ طلبك بدفعٍ إلكترونيّ آمن. التوصيل خلال ٢-٥ أيام عمل لجميع مناطق المملكة.'
          : 'Tamrat specialises in authentic, carefully selected Saudi dates. You browse the varieties, pick your size (quarter kilo, half kilo, one kilo, or a gift box) and check out with secure online payment. Delivery is 2–5 business days across the Kingdom, with free shipping over 250 SAR.')}
      </p>
      <div style={{ margin: '14px 0 4px' }}>
        <Link to="/shopping" style={{ display: 'inline-block', background: GREEN, color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 16, padding: '13px 30px', borderRadius: 12 }}>
          {isAr ? 'تصفّح التشكيلة كاملة' : 'Browse all dates'}
        </Link>
      </div>

      <h2 style={h2}>{isAr ? 'أسئلة شائعة' : 'Frequently asked questions'}</h2>
      {FAQS.map((f, i) => (
        <div key={i} style={{ padding: '14px 0', borderBottom: '1px solid #eceef0' }}>
          <h3 style={{ fontSize: 16.5, fontWeight: 700, color: '#1a1a1a', margin: '0 0 6px' }}>{isAr ? f.q_ar : f.q_en}</h3>
          <p style={{ ...p, margin: 0, fontSize: 15.5 }}>{linkifyBrand(isAr ? f.a_ar : f.a_en)}</p>
        </div>
      ))}
    </div>
  );
};

export default DatesGuide;
